import { useEffect, useMemo, useState } from "react";
import { Search, Filter, BookOpen, User, Calendar, MapPin, X, Lock } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import PdfViewer from "@/components/PdfViewer";

const TYPES = [
  { value: "", label: "Tous les types" },
  { value: "ouvrage", label: "Ouvrage" },
  { value: "revue", label: "Revue" },
  { value: "notes_cours", label: "Notes de cours" },
  { value: "essai", label: "Essai" },
  { value: "roman", label: "Roman" },
  { value: "article", label: "Article" },
  { value: "thèse", label: "Thèse" },
];

const TYPE_LABELS = TYPES.reduce((a, t) => { a[t.value] = t.label; return a; }, {});

const DEFAULTS = {
  eyebrow: "Catalogue éditorial PUSIC",
  title: "Publications universitaires",
  banner_image: "/media/digital-library.jpg",
  intro:
    "Découvrez le catalogue des publications éditées par les Presses Universitaires des Sciences de l'Information et de la Communication (PUSIC).",
};

export default function PusicPublications() {
  const [content, setContent] = useState(DEFAULTS);
  const [q, setQ] = useState("");
  const [docType, setDocType] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    api.get("/content/pusic.publications").then((r) => setContent({ ...DEFAULTS, ...(r.data || {}) })).catch(() => {});
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (docType) params.doc_type = docType;
      const { data } = await api.get("/publications", { params });
      setItems(data);
    } finally { setLoading(false); }
  };

  const counts = useMemo(() => {
    const c = {};
    for (const t of TYPES) if (t.value) c[t.value] = 0;
    items.forEach((p) => { if (c[p.doc_type] !== undefined) c[p.doc_type]++; });
    return c;
  }, [items]);

  return (
    <div data-testid="pusic-publications-page">
      <section className="relative h-[42vh] min-h-[300px] overflow-hidden">
        <img src={content.banner_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-veil" />
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-12">
          <div className="eyebrow" style={{ color: "#E5C158" }}>{content.eyebrow}</div>
          <h1 className="mt-3 text-white text-4xl sm:text-5xl font-bold tracking-tight">{content.title}</h1>
          <p className="mt-3 text-white/85 max-w-3xl">{content.intro}</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-unisic-gold" />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="paper-card p-6" data-testid="pusic-search">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Rechercher</label>
              <div className="mt-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Titre, auteur, mot-clé..." className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="pusic-q" />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Type d'ouvrage</label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white" data-testid="pusic-type">
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}{t.value && counts[t.value] ? ` (${counts[t.value]})` : ""}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-5 flex justify-between items-center">
            <div className="text-xs text-slate-500 flex items-center gap-2"><Filter className="w-3 h-3" /> {items.length} publication(s)</div>
            <button type="submit" className="btn-primary text-sm" data-testid="pusic-btn-search"><Search className="w-4 h-4" /> Rechercher</button>
          </div>
        </form>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {loading && <div className="col-span-full text-center py-12 text-slate-500">Chargement...</div>}
          {!loading && items.length === 0 && (
            <div className="col-span-full text-center py-16 paper-card p-10">
              <BookOpen className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="mt-4 text-slate-500">Aucune publication pour ces critères.</p>
            </div>
          )}
          {items.map((p) => (
            <div key={p.id} className="paper-card card-hover p-6 flex flex-col" data-testid={`pusic-item-${p.id}`}>
              {p.cover_image ? (
                <img src={p.cover_image} alt="" className="w-full h-52 object-cover rounded-sm mb-4" />
              ) : (
                <div className="w-full h-52 bg-gradient-to-br from-unisic-blue/90 to-unisic-blue rounded-sm mb-4 flex items-center justify-center">
                  <BookOpen className="w-12 h-12 text-white/70" />
                </div>
              )}
              <div className="text-[11px] uppercase tracking-widest text-unisic-gold font-semibold flex items-center gap-2">
                {TYPE_LABELS[p.doc_type] || "Ouvrage"}
                {p.requires_subscription && <span className="inline-flex items-center gap-1 bg-unisic-gold/15 text-unisic-blue px-2 py-0.5 rounded"><Lock className="w-3 h-3" /> Abonnement</span>}
              </div>
              <h3 className="mt-2 font-display font-semibold text-unisic-blue leading-snug">{p.title}</h3>
              {p.abstract && <p className="mt-3 text-sm text-slate-600 line-clamp-3 flex-1">{p.abstract}</p>}
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-1.5 text-xs text-slate-600">
                <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-slate-400" /> {p.authors || "—"}</div>
                <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-slate-400" /> {p.year || "—"}</div>
                <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {p.city || "Kinshasa"}</div>
              </div>
              {p.file_path && !p.requires_subscription && (
                <button onClick={() => setViewer({ title: p.title, url: fileUrl(p.file_path), filename: `${p.title}.pdf` })} className="mt-5 btn-secondary text-sm justify-center" data-testid={`pusic-read-${p.id}`}>
                  <BookOpen className="w-4 h-4" /> Consulter
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center px-2 sm:px-6 py-6 overflow-auto" onClick={() => setViewer(null)}>
          <div className="bg-white max-w-4xl w-full rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-display font-semibold text-unisic-blue truncate">{viewer.title}</h3>
              <button onClick={() => setViewer(null)} className="p-2 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4"><PdfViewer url={viewer.url} filename={viewer.filename} /></div>
          </div>
        </div>
      )}
    </div>
  );
}
